import ListAltIcon from '@mui/icons-material/ListAlt';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Tab from '@mui/material/Tab';
import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useParams } from 'react-router-dom';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { useSearchParamsState } from '../../hooks';
import { appConfigSelector } from '../../redux/modules/appConfig/appConfig.selectors';
import { locations } from '../../routes/locations';
import FeeTable from '../Settings/FeeTable';
import { AlpacaJournalTab } from './AlpacaJournalTab';
import { AccumulatedAumReportPage } from './AccumulatedAumReportPage';
import { AumReportPage } from './AumReportPage';
import { CashTransactionsReportPage } from './CashTransactionsReportPage';
import { CashTab } from './CashTab';
import { CMAUsersReportPage } from './CMAUsersReportPage';
import { OrdersReportPage } from './OrdersReportPage';
import { PortfolioDetailsReportPage } from './PortfolioDetailsReportPage';
import { PortfolioOrdersDataReportPage } from './PortfolioOrdersDataReportPage';
import { PortfolioTotalsReportPage } from './PortfolioTotalsReportPage';
import { AlpacaOrdersFxRevenueTab } from './FxRevenue/AlpacaOrdersFxRevenueTab';
import { AlpacaTransfersFxRevenueTab } from './FxRevenue/AlpacaTransfersFxRevenueTab';
import { JadwaOrdersFxRevenueTab } from './FxRevenue/JadwaOrdersFxRevenueTab';
import { JadwaTransfersFxRevenueTab } from './FxRevenue/JadwaTransfersFxRevenueTab';
import { PortfolioOrderExecutionsTab } from './PortfolioOrderExecutionsTab';
import {
  isReportHubSegment,
  isStandaloneSidebarReportSegment,
  REPORT_SEGMENT_CASH_TRANSACTIONS,
  REPORT_SEGMENT_CMA_USERS,
  REPORT_SEGMENT_ORDERS,
  REPORT_SEGMENT_PORTFOLIO_ORDERS_DATA,
  REPORT_SEGMENT_AUM,
  REPORT_SEGMENT_ACCUMULATED_AUM,
  REPORT_SEGMENT_PORTFOLIO_DETAILS,
  REPORT_SEGMENT_PORTFOLIO_TOTALS,
  REPORT_SEGMENT_USERS,
  tabParamByReportSegment,
} from './reportRouteSegments';
import { UnitsTab } from './UnitsTab';
import UserIndexFundTab from './UserIndexFundTab';
import { UsersReportPage } from './UsersReportPage';

const mapTabParamToValue: { [prop: string]: string } = {
  portfolioOrderExecutions: '1',
  cash: '2',
  units: '3',
  alpacaOrdersFxRevenue: '4',
  alpacaTransfersFxRevenue: '5',
  jadwaOrdersFxRevenue: '6',
  jadwaTransfersFxRevenue: '7',
  userIndexFund: '8',
  alpacaJournal: '9',
  fee: '10',
};
const mapTabValueToTabParam: { [prop: string]: string } = {
  1: 'portfolioOrderExecutions',
  2: 'cash',
  3: 'units',
  4: 'alpacaOrdersFxRevenue',
  5: 'alpacaTransfersFxRevenue',
  6: 'jadwaOrdersFxRevenue',
  7: 'jadwaTransfersFxRevenue',
  8: 'userIndexFund',
  9: 'alpacaJournal',
  10: 'fee',
};

function ReportHub() {
  const [selectedTab, setSelectedTab] = useSearchParamsState('tab', 'portfolioOrderExecutions');

  const { fetchStatus, updateStatus } = useSelector(appConfigSelector);

  const isLoading = fetchStatus === fetchStatuses.pending || updateStatus === fetchStatuses.pending;

  const normalizedTabParam = selectedTab in mapTabParamToValue ? selectedTab : 'portfolioOrderExecutions';
  const tabContextValue = mapTabParamToValue[normalizedTabParam];

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    const newTab = mapTabValueToTabParam[newValue];
    setSelectedTab(newTab);
    if (newTab !== 'userIndexFund') {
      const url = new URL(window.location.href);
      url.searchParams.delete('userIndexFundTab');
      window.history.replaceState({}, '', url.toString());
    }
  };

  return (
    <Container component="main" maxWidth="xl">
      <TabContext value={tabContextValue}>
        <Box>
          <TabList
            value={tabContextValue}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            aria-label="Report tabs"
          >
            <Tab
              icon={<ListAltIcon />}
              iconPosition="start"
              label="Portfolio Order Executions"
              value="1"
              sx={{ textTransform: 'capitalize' }}
              disableRipple
              disabled={isLoading}
            />
            <Tab
              icon={<ListAltIcon />}
              iconPosition="start"
              label="Cash"
              value="2"
              sx={{ textTransform: 'capitalize' }}
              disableRipple
              disabled={isLoading}
            />
            <Tab
              icon={<ListAltIcon />}
              iconPosition="start"
              label="Units"
              value="3"
              sx={{ textTransform: 'capitalize' }}
              disableRipple
              disabled={isLoading}
            />
            <Tab
              icon={<ListAltIcon />}
              iconPosition="start"
              label="Alpaca Orders Fx Revenue"
              value="4"
              sx={{ textTransform: 'capitalize' }}
              disableRipple
              disabled={isLoading}
            />
            <Tab
              icon={<ListAltIcon />}
              iconPosition="start"
              label="Alpaca Transfers Fx Revenue"
              value="5"
              sx={{ textTransform: 'capitalize' }}
              disableRipple
              disabled={isLoading}
            />
            <Tab
              icon={<ListAltIcon />}
              iconPosition="start"
              label="Jadwa Orders Fx Revenue"
              value="6"
              sx={{ textTransform: 'capitalize' }}
              disableRipple
              disabled={isLoading}
            />
            <Tab
              icon={<ListAltIcon />}
              iconPosition="start"
              label="Jadwa Transfers Fx Revenue"
              value="7"
              sx={{ textTransform: 'capitalize' }}
              disableRipple
              disabled={isLoading}
            />
            <Tab
              icon={<ListAltIcon />}
              iconPosition="start"
              label="User Index Fund"
              value="8"
              sx={{ textTransform: 'capitalize' }}
              disableRipple
              disabled={isLoading}
            />
            <Tab
              icon={<ListAltIcon />}
              iconPosition="start"
              label="Alpaca Journal"
              value="9"
              sx={{ textTransform: 'capitalize' }}
              disableRipple
              disabled={isLoading}
            />
            <Tab
              icon={<ListAltIcon />}
              iconPosition="start"
              label="Fee"
              value="10"
              sx={{ textTransform: 'capitalize' }}
              disableRipple
              disabled={isLoading}
            />
          </TabList>
        </Box>
        <TabPanel value="1">
          <PortfolioOrderExecutionsTab />
        </TabPanel>
        <TabPanel value="2">
          <CashTab />
        </TabPanel>
        <TabPanel value="3">
          <UnitsTab />
        </TabPanel>
        <TabPanel value="4">
          <AlpacaOrdersFxRevenueTab />
        </TabPanel>
        <TabPanel value="5">
          <AlpacaTransfersFxRevenueTab />
        </TabPanel>
        <TabPanel value="6">
          <JadwaOrdersFxRevenueTab />
        </TabPanel>
        <TabPanel value="7">
          <JadwaTransfersFxRevenueTab />
        </TabPanel>
        <TabPanel value="8">
          <UserIndexFundTab />
        </TabPanel>
        <TabPanel value="9">
          <AlpacaJournalTab />
        </TabPanel>
        <TabPanel value="10">
          <FeeTable />
        </TabPanel>
      </TabContext>
    </Container>
  );
}

export function Report() {
  const { reportSegment } = useParams<{ reportSegment: string }>();

  if (!reportSegment) {
    return null;
  }

  if (isReportHubSegment(reportSegment)) {
    return <ReportHub />;
  }

  if (reportSegment === REPORT_SEGMENT_CASH_TRANSACTIONS) {
    return (
      <Container component="main" maxWidth="xl">
        <CashTransactionsReportPage />
      </Container>
    );
  }

  if (reportSegment === REPORT_SEGMENT_USERS) {
    return (
      <Container component="main" maxWidth="xl">
        <UsersReportPage />
      </Container>
    );
  }

  if (reportSegment === REPORT_SEGMENT_ORDERS) {
    return (
      <Container component="main" maxWidth="xl">
        <OrdersReportPage />
      </Container>
    );
  }

  if (reportSegment === REPORT_SEGMENT_PORTFOLIO_ORDERS_DATA) {
    return (
      <Container component="main" maxWidth="xl">
        <PortfolioOrdersDataReportPage />
      </Container>
    );
  }

  if (reportSegment === REPORT_SEGMENT_AUM) {
    return (
      <Container component="main" maxWidth="xl">
        <AumReportPage />
      </Container>
    );
  }

  if (reportSegment === REPORT_SEGMENT_ACCUMULATED_AUM) {
    return (
      <Container component="main" maxWidth="xl">
        <AccumulatedAumReportPage />
      </Container>
    );
  }

  if (reportSegment === REPORT_SEGMENT_PORTFOLIO_DETAILS) {
    return (
      <Container component="main" maxWidth="xl">
        <PortfolioDetailsReportPage />
      </Container>
    );
  }

  if (reportSegment === REPORT_SEGMENT_PORTFOLIO_TOTALS) {
    return (
      <Container component="main" maxWidth="xl">
        <PortfolioTotalsReportPage />
      </Container>
    );
  }

  if (reportSegment === REPORT_SEGMENT_CMA_USERS) {
    return (
      <Container component="main" maxWidth="xl">
        <CMAUsersReportPage />
      </Container>
    );
  }

  if (isStandaloneSidebarReportSegment(reportSegment)) {
    return <Container component="main" maxWidth="xl" />;
  }

  if (reportSegment in tabParamByReportSegment) {
    const tabParam = tabParamByReportSegment[reportSegment];
    return <Navigate to={`${locations.reportHub()}?tab=${encodeURIComponent(tabParam)}`} replace />;
  }

  return <Navigate to={locations.reportHub()} replace />;
}
